/**
 * Shared document workspace — package-owned types.
 *
 * The package owns the tab/group/layout/window vocabulary for every suite
 * application; an application only declares surfaces (renderers) and drives
 * the store through the actions of `createDocumentWorkspace`.
 */

import type { StoreApi, UseBoundStore } from 'zustand';

/** Edge/corner drop zones a floating document window can snap (dock) to */
export type DocSnapZone =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/** How the workspace arranges the docked document windows */
export type DocArrangement = 'tabs' | 'tile' | 'cascade' | 'float';

/** Direction of a two-panel split */
export type SplitDirection = 'horizontal' | 'vertical';

/** Directional resize grips of a floating window */
export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * One entry of the shared tab model. Chrome-relevant state only — the
 * document's CONTENT stays app-side (looked up via `fileRef` or the app's
 * own stores inside the surface renderer).
 */
export interface DocTab {
  id: string;
  /** App-declared document kind; selects the surface renderer + icon */
  kind: string;
  /** English fallback label; resolved through `resolveLabel` when labelKey is set */
  label: string;
  labelKey?: string;
  /** App-opaque reference (path, handle, app-store id) */
  fileRef?: unknown;
  /** Mirror of the app's dirty flag; drives the tab dirty dot */
  isDirty?: boolean;
  /** Pinned tabs are kept in place (kor heritage, suite-wide new) */
  isPinned?: boolean;
  /** Per-tab app state bag (kor's `Tab.state` shape) */
  state?: Record<string, unknown>;
}

/** A named group of documents sharing one tab strip */
export interface DocGroup {
  id: string;
  /** Ordered list of tab ids shown in this group's strip */
  tabIds: string[];
  /** The tab currently displayed in this group's pane */
  activeTabId: string | null;
}

/**
 * Recursive binary split tree. A leaf hosts one editor group; a split node
 * divides the area into two children with percentage sizes. pik's two-group
 * `PanelLayout` is a depth-1 tree of this shape. Split nodes carry a stable
 * `id` so divider drags can address their node across renders.
 */
export type DocLayoutNode =
  | { type: 'leaf'; groupId: string }
  | {
      type: 'split';
      id: string;
      direction: SplitDirection;
      sizes: [number, number];
      children: [DocLayoutNode, DocLayoutNode];
    };

/** Per-tab presentation state: docked into a group, or floating with geometry */
export interface DocWindowState {
  mode: 'tab' | 'float';
  /** Floating position in float-root coordinates (only when mode === 'float') */
  position?: { x: number; y: number };
  /** Floating size, px or CSS units like '100%' (only when mode === 'float') */
  size?: { width: number | string; height: number | string };
  /** Zone this floating window is snapped to; geometry is re-derived from the
      zone whenever the float root is resized */
  dockZone?: DocSnapZone;
  /** Geometry saved before a maximize so it can be restored afterwards */
  restoreState?: {
    position?: { x: number; y: number };
    size?: { width: number | string; height: number | string };
  };
  /** Which group this tabbed document belongs to (undefined when floating) */
  groupId?: string;
}

/** A closed tab kept for reopen (bounded stack) */
export interface ClosedTabEntry {
  tab: DocTab;
  groupId: string | null;
  index: number;
}

/** Optional app-side adapter for document-scoped state swapping. The shared
 * stores of an app only hold the ACTIVE document's state; the adapter swaps
 * snapshots in and out around every activation (pik's snapshot pattern). */
export interface DocumentSnapshotAdapter {
  /** Called before the active tab switches away */
  capture: (tabId: string) => void;
  /** Called after a tab becomes active */
  restore: (tabId: string) => void;
  /** Called when a tab is closed */
  release: (tabId: string) => void;
}

/** A surface renderer declaration: how the app draws one document kind */
export interface SurfaceDefinition {
  kind: string;
  render: (ctx: SurfaceRenderContext) => React.ReactNode;
  /** Keep the surface mounted while its tab is inactive (heavy engines that
      cannot cheaply remount). Default false. */
  keepMounted?: boolean;
}

/** Context passed to a surface renderer */
export interface SurfaceRenderContext {
  tab: DocTab;
  /** Creation-order index of the tab (0-based) */
  index: number;
  isActive: boolean;
  /** False when the window must ignore pointer events (overlapped in cascade) */
  interactive: boolean;
  /** True when the window is torn out and floats freely (title bar visible) */
  floating: boolean;
}

/** Class-name roles of the shared chrome. Every role REPLACES the package
 * default when provided, so an app can map the chrome onto its legacy CSS
 * (pik does) or take the default `doc-*` vocabulary. */
export interface WorkspaceClassNames {
  emptyState?: string;
  groupPane?: string;
  windowsArea?: (arrangement: DocArrangement) => string;
  splitContainer?: (direction: SplitDirection) => string;
  splitDivider?: (direction: SplitDirection) => string;
  dividerDragging?: string;
  tabStrip?: string;
  tab?: string;
  tabActive?: string;
  tabDragging?: string;
  tabDockPreview?: string;
  tabLabel?: string;
  tabDirty?: string;
  tabPin?: string;
  dropIndicator?: string;
  emptyTabsHint?: string;
  window?: string;
  windowInactive?: string;
  windowTitleBar?: string;
  windowTitle?: string;
  windowTitleControls?: string;
  windowTitleButton?: string;
  resizeHandle?: (dir: ResizeDirection) => string;
  snapPreview?: (zone: DocSnapZone, fullscreen: boolean) => string;
  overflowButton?: string;
  overflowMenu?: string;
  overflowItem?: string;
  overflowItemActive?: string;
}

/** Opt-in capabilities on top of the pik reference behavior */
export interface WorkspaceFeatures {
  /** Render a dirty dot on tabs whose `isDirty` is set. Default false
      (pik keeps its exact look; file apps turn this on). */
  dirtyDot?: boolean;
  /** Show the pin glyph on pinned tabs. Default false. */
  pinning?: boolean;
  /** Middle-click on a tab requests its close. Default false. */
  middleClickClose?: boolean;
  /** Ctrl+Tab / Ctrl+Shift+Tab cycle, Ctrl+W / Ctrl+F4 close through
      onCloseRequest. Default false. */
  shortcuts?: boolean;
  /** Chevron + list popup when the tab strip overflows. Default false. */
  overflowMenu?: boolean;
}

/** Title-bar labels (defaults are English; an app bridges its i18n) */
export interface WorkspaceLabels {
  dockAsTab: (label: string) => string;
  dockAsTabAria: (label: string) => string;
  maximizeAria: (label: string) => string;
  closeTabAria: (label: string) => string;
  closeWindowAria: (label: string) => string;
  overflowLabel: string;
}

export interface DocumentWorkspaceOptions {
  /** Persisted-key namespace, e.g. 'pik' → `pik.doc_workspace` */
  storageNamespace: string;
  classNames?: WorkspaceClassNames;
  features?: WorkspaceFeatures;
  labels?: Partial<WorkspaceLabels>;
  /** Persist the open-tab set, arrangement, splits and float geometry across
      restarts (opt-in; the reference app pik keeps its startup behavior). */
  sessionRestore?: boolean;
  /** Called for every tab restored from a session so the app can reload its
      content (file handles, engine state). */
  onSessionRestore?: (tab: DocTab) => void;
  /** Called when a session snapshot exists but the store was empty. */
}

/** The factory result: everything an app touches */
export interface DocumentWorkspace {
  namespace: string;
  /** Canonical document-tab store (zustand). The single source of truth for
   * tab strips, groups, splits, floating windows and arrangements. */
  store: UseBoundStore<StoreApi<DocumentTabsState>>;
  classNames: WorkspaceClassNames;
  features: Required<WorkspaceFeatures>;
  labels: WorkspaceLabels;
  registerSurface: (def: SurfaceDefinition) => void;
  getSurface: (kind: string) => SurfaceDefinition | undefined;
  setSnapshotAdapter: (adapter: DocumentSnapshotAdapter | null) => void;
  getSnapshotAdapter: () => DocumentSnapshotAdapter | null;
}

/* ------------------------------------------------------------------ */
/* Canonical store state (owned here so the shell and the factory share */
/* one vocabulary; the zustand store is built in the factory)           */
/* ------------------------------------------------------------------ */

export interface OpenTabOptions {
  groupId?: string;
  index?: number;
  activate?: boolean;
}

export interface DocumentTabsState {
  tabs: Record<string, DocTab>;
  /** Creation order — mirrors the reference app's `files` array semantics */
  tabIds: string[];
  groups: Record<string, DocGroup>;
  layout: DocLayoutNode;
  windowStates: Record<string, DocWindowState>;
  arrangement: DocArrangement;
  activeTabId: string | null;
  closedStack: ClosedTabEntry[];

  openTab: (tab: DocTab, options?: OpenTabOptions) => void;
  closeTab: (id: string) => void;
  activateTab: (id: string) => void;
  updateTab: (
    id: string,
    patch: Partial<Pick<DocTab, 'label' | 'labelKey' | 'isDirty' | 'isPinned' | 'state' | 'kind' | 'fileRef'>>,
  ) => void;
  setDirty: (id: string, dirty: boolean) => void;
  pinTab: (id: string, pinned?: boolean) => void;
  setWindowState: (id: string, patch: Partial<DocWindowState>) => void;
  setArrangement: (arrangement: DocArrangement) => void;
  floatTab: (
    id: string,
    geometry?: { position: { x: number; y: number }; size: { width: number; height: number } },
  ) => void;
  dockTab: (id: string, groupId?: string, index?: number) => void;
  splitWithTab: (id: string, direction: SplitDirection, position: 'before' | 'after') => void;
  setNodeSplitSizes: (
    node: Extract<DocLayoutNode, { type: 'split' }>,
    sizes: [number, number],
  ) => void;
  collapseEmptyGroups: () => void;
  reorderInGroup: (groupId: string, fromIndex: number, toIndex: number) => void;
  reopenClosedTab: () => DocTab | null;
  /** The focused group (kor-style multi-pane: each group keeps its own
   * active tab; this tracks which group the user is working in) */
  activeGroupId: string | null;
  setActiveGroup: (groupId: string) => void;
  /** Set a group's own active tab without changing the global active tab */
  setGroupActiveTab: (groupId: string, tabId: string) => void;
  /** Split a leaf in the given direction WITHOUT moving any tab; returns the
   * new (empty) group id. The caller fills it via openTab/dockTab. */
  splitGroup: (groupId: string, direction: SplitDirection, position: 'before' | 'after') => string | null;
}

/** Default group id used by a fresh workspace */
export const DEFAULT_GROUP_ID = 'suite-default';

/** Closed-tab stack bound */
export const CLOSED_STACK_LIMIT = 10;
